import { useMemo } from 'react';
import styled from 'styled-components';

import { AdminListPageConfig, AdminListPageRenderer, useAdminListPage } from 'hooks/useAdminListPage';
import { simpleDateAtTime } from 'lib/date';
import { DISPLAY_TEXT, FAMILY_LABELS, OPTION_LIST, WorkflowLookup } from 'models/WorkflowLookup';
import { adminIndexClients } from 'services/api/admin/clients';
import {
  adminWorkflowLookupsQueryKeys,
  destroyAdminWorkflowLookup,
  indexAdminWorkflowLookups,
  indexAdminWorkflowLookupWorkflows,
  showAdminWorkflowLookup,
} from 'services/api/admin/workflowLookups';
import { colors } from 'styles/theme/colors';
import { BodySmall } from 'styles/typography';
import CopyIcon from 'svg/CopyIcon';
import PencilIconOutline from 'svg/PencilIconOutline';
import TrashIcon from 'svg/TrashIcon';

import CountLink from '../shared/CountLink';

import CustomizeForClientModal from './CustomizeForClientModal';
import WorkflowLookupSlideout from './WorkflowLookupSlideout';

// Admin → Workflows → Lookups (OLIO-2830). Every list the workflow engine reads its words from:
// the options a picker offers, and the names of a workflow's actions, values and categories.
//
// One row per list per scope. A client's own row replaces the global one for that client, so the
// client filter deliberately shows both — a key they have not customized still resolves to the
// Olio default for them, and hiding it would hide most of what they are actually using.
const config: AdminListPageConfig<WorkflowLookup> = {
  title: 'Workflow Lookups',
  columnStorageKey: 'admin.workflowLookups',
  queryKey: adminWorkflowLookupsQueryKeys.index(),
  fetchFn: indexAdminWorkflowLookups,
  fetchParams: { defaultSort: 'key asc' },
  showResultCount: true,
  // Matched against the label, the workflow and the key, not a column — Workflow::LookupRegistry
  // resolves the term to a set of keys the same way it resolves the two dropdowns.
  searchPlaceholder: 'Search lookups',

  filters: [
    {
      key: 'clientId',
      label: 'Client',
      type: 'dropdown',
      fetchConfig: {
        queryKey: ['adminClients', 'workflowLookups', 'filter'],
        fetchFn: adminIndexClients,
        params: { sortBy: 'name asc' },
      },
      getOptionLabel: (option: any) => option.name,
      getOptionValue: (option: any) => option.id,
    },
    {
      // Narrows to one workflow's display text. Option lists belong to no workflow, so choosing one
      // hides them — which is the point: it answers "what is Referral Intake's wording?"
      key: 'workflowApiName',
      label: 'Workflow',
      type: 'dropdown',
      fetchConfig: {
        queryKey: ['adminWorkflowLookupWorkflows'],
        fetchFn: indexAdminWorkflowLookupWorkflows,
      },
      getOptionLabel: (option: any) => option.name,
      getOptionValue: (option: any) => option.id,
    },
    {
      key: 'family',
      label: 'Type',
      type: 'dropdown',
      staticOptions: [
        { id: OPTION_LIST, name: FAMILY_LABELS[OPTION_LIST] },
        { id: DISPLAY_TEXT, name: FAMILY_LABELS[DISPLAY_TEXT] },
      ],
      getOptionLabel: (option: any) => option.name,
      getOptionValue: (option: any) => option.id,
    },
  ],

  columns: [
    {
      id: 'key',
      header: 'List',
      sortable: true,
      size: 340,
      // The workflow, not the label, is what disambiguates a row here: three workflows each have a
      // "Action names" list, so the name alone does not say which is which. The underlying key is
      // engineer vocabulary and earns its space only once you are editing the list, so it lives in
      // the slideout header instead.
      render: (row, ctx) => (
        <div>
          <ListName onClick={() => ctx.openSlideout('items', row)}>{row.label}</ListName>
          {row.workflowLabel && <Subtitle>{row.workflowLabel}</Subtitle>}
        </div>
      ),
    },
    {
      id: 'family',
      header: 'Type',
      render: (row) => FAMILY_LABELS[row.family] ?? 'Unused',
    },
    {
      // Its own column rather than a line under the name: the name's subtitle already carries the
      // workflow, and a relationship reading as prose in the same slot looked like the same kind of
      // fact. Only the child direction is listed — the parent's side is the same pair seen from the
      // other row, and it is the child's dependency you need before editing one.
      id: 'dependsOn',
      header: 'Depends on',
      render: (row) => row.dependsOn?.label ?? '',
    },
    {
      id: 'client',
      header: 'Scope',
      sortable: true,
      // Same idea as the edited-permissions marker on the users list: the cell says what the row's
      // baseline is, and the marker says it no longer matches. Only display text can differ from a
      // default, so the count is 0 everywhere else and nothing renders.
      render: (row) => (
        <ScopeCell>
          {row.global ? 'Olio default' : row.clientName}
          {row.customizedItemCount > 0 && (
            <Customized title={`${row.customizedItemCount} renamed from the Olio default`}>
              <PencilIconOutline width={13} height={13} />
              {row.customizedItemCount}
            </Customized>
          )}
        </ScopeCell>
      ),
    },
    {
      id: 'activeItemCount',
      header: 'Options',
      render: (row, ctx) => <CountLink onClick={() => ctx.openSlideout('items', row)}>{row.activeItemCount}</CountLink>,
    },
    {
      id: 'updatedBy',
      header: 'Last edited by',
      sortable: true,
      render: (row) => row.updatedByName ?? <Muted>Not edited</Muted>,
    },
    {
      id: 'updatedAt',
      header: 'Last updated',
      sortable: true,
      render: (row) => simpleDateAtTime(row.updatedAt),
    },
  ],

  rowActions: [
    {
      label: 'Edit list',
      icon: <PencilIconOutline width={18} height={18} />,
      onClick: (row, ctx) => ctx.openSlideout('items', row),
    },
    {
      // Copy, because that is what it does: the client's version starts as a copy of the Olio
      // default's current options.
      label: 'Customize for client',
      icon: <CopyIcon width={18} height={18} />,
      permission: 'adminWorkflowLookupCreate',
      visible: (row) => row.global && row.itemsEditable && row.editable,
      onClick: (row, ctx) => ctx.openModal('customize', row),
    },
    {
      label: 'Remove customization',
      permission: 'adminWorkflowLookupDelete',
      danger: true,
      icon: <TrashIcon />,
      // Never offered on the Olio default: it is what every client without their own falls back
      // to, so removing it would empty the picker for all of them rather than reset anyone.
      visible: (row) => !row.global,
      onClick: (row, ctx) => ctx.openModal('removeCustomization', row),
    },
  ],

  modals: {
    removeCustomization: {
      actionText: 'remove',
      objType: 'customized list',
      // The paired sentence is conditional rather than row-specific: the shared modal takes a static
      // string, and whether this client customized the OTHER half is a fact the index would need an
      // extra per-row query to serve. Worth revisiting if lists start commonly coming in pairs.
      subText:
        'This client goes back to the Olio default list. If they also customized a list this one is paired with, that ' +
        'is reset too, since half a pair leaves the other half pointing at entries that are no longer there. Any ' +
        'option only they had stops resolving, so a referral that recorded one will show its short name instead.',
      // The shared confirm handler closes the modal unconditionally (useAdminListPage.tsx:745) and
      // only gates the refetch on the result, so a failure is reported by the toast, not by the
      // modal staying open. Returning false is what stops the refetch: nothing was deleted, so
      // re-fetching would only redraw the same row and make the failure look like a no-op.
      onConfirm: async (row: WorkflowLookup, ctx) => {
        try {
          await destroyAdminWorkflowLookup(row.id);
        } catch (error: unknown) {
          const message = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
          ctx.toast(message ?? 'That customization could not be removed.');

          return false;
        }

        ctx.toast('Customization removed.');
      },
    },
  },

  slideouts: {
    items: {
      routeBasePath: '/admin/workflows/lookups',
      fetchRow: (id: string) => showAdminWorkflowLookup(id),
      render: () => null,
    },
  },
};

export default function WorkflowLookups() {
  // Memoised because useAdminListPage keys its deep-link effect on `config.slideouts`. A fresh
  // object each render re-ran that effect and re-issued the row fetch behind a deep link, several
  // times per load. The sibling admin pages keep their config at module scope for the same reason;
  // this one cannot, because its render closures need component scope.
  const pageConfig: AdminListPageConfig<WorkflowLookup> = useMemo(
    () => ({
      ...config,
      slideouts: {
        items: {
          ...config.slideouts!.items,
          render: (row: WorkflowLookup, onClose: () => void) => (
            <WorkflowLookupSlideout lookup={row} onClose={onClose} />
          ),
        },
      },
      customModals: {
        customize: {
          render: (row: WorkflowLookup, show: boolean, onClose: () => void, ctx) => (
            <CustomizeForClientModal
              lookup={row}
              show={show}
              onClose={onClose}
              onCreated={(created) => {
                ctx.refetch();
                ctx.openSlideout('items', created);
              }}
            />
          ),
        },
      },
    }),
    []
  );

  const state = useAdminListPage(pageConfig);

  return <AdminListPageRenderer config={pageConfig} state={state} />;
}

// 500, matching the other admin tables' primary column (see PortalSystems). A <strong> here
// renders 700 and reads far heavier than the rest of the admin.
const ListName = styled.span`
  font-weight: 500;
  color: ${colors.blue100};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const Muted = styled(BodySmall)`
  color: ${colors.black50};
`;

// One line, ellipsised, full value on hover. Wrapping these pushed every display-text row to two
// lines and broke keys mid-word, which reads worse than truncating them.
const ScopeCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Customized = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  color: ${colors.black50};
  font-size: 12px;
`;

const Subtitle = styled(BodySmall)`
  display: block;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  color: ${colors.black50};
`;
